import React, { useMemo } from "react";
import { Paper, Typography } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { WEEKDAYS } from "../../pages/Dashboard/format";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  title: { fontWeight: 600 },
  subtitle: { color: theme.palette.text.secondary, marginBottom: theme.spacing(1.5) },
  scroll: { overflowX: "auto", ...theme.scrollbarStyles },
  grid: {
    display: "grid",
    gridTemplateColumns: "36px repeat(24, minmax(18px, 1fr))",
    gap: 2,
    minWidth: 520,
  },
  dayLabel: {
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
  },
  hourLabel: {
    fontSize: "0.65rem",
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
  cell: {
    height: 18,
    borderRadius: 3,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: theme.spacing(1),
    fontSize: "0.7rem",
    color: theme.palette.text.secondary,
    justifyContent: "flex-end",
  },
}));

/** Mapa de calor: conversas abertas por dia da semana × hora. */
export default function Heatmap({ heatmap = [] }) {
  const classes = useStyles();
  const theme = useTheme();

  const { matrix, max } = useMemo(() => {
    const m = Array.from({ length: 7 }, () => Array(24).fill(0));
    let top = 0;
    heatmap.forEach(({ dow, hour, total }) => {
      if (dow >= 1 && dow <= 7 && hour >= 0 && hour < 24) {
        m[dow - 1][hour] += total;
        top = Math.max(top, m[dow - 1][hour]);
      }
    });
    return { matrix: m, max: top };
  }, [heatmap]);

  // Escala sequencial de um só tom (azul primário), do claro ao escuro.
  const cellColor = (value) => {
    if (!value) return theme.palette.action.hover;
    const alpha = 0.18 + 0.82 * (value / max);
    const hex = Math.round(alpha * 255).toString(16).padStart(2, "0");
    return `${theme.palette.primary.main}${hex}`;
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="subtitle1" className={classes.title}>
        Horários de pico
      </Typography>
      <Typography variant="caption" component="div" className={classes.subtitle}>
        Conversas abertas por dia da semana e hora
      </Typography>
      <div className={classes.scroll}>
        <div className={classes.grid}>
          <span />
          {Array.from({ length: 24 }, (_, h) => (
            <span key={h} className={classes.hourLabel}>
              {h % 3 === 0 ? `${h}h` : ""}
            </span>
          ))}
          {matrix.map((row, d) => (
            <React.Fragment key={d}>
              <span className={classes.dayLabel}>{WEEKDAYS[d]}</span>
              {row.map((value, h) => (
                <span
                  key={h}
                  className={classes.cell}
                  style={{ backgroundColor: cellColor(value) }}
                  title={`${WEEKDAYS[d]} ${String(h).padStart(2, "0")}h: ${value} conversa${value === 1 ? "" : "s"}`}
                />
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className={classes.legend}>
        <span>Menos</span>
        {[0.18, 0.45, 0.72, 1].map((a) => (
          <span
            key={a}
            className={classes.cell}
            style={{
              width: 14,
              height: 10,
              backgroundColor: `${theme.palette.primary.main}${Math.round(a * 255)
                .toString(16)
                .padStart(2, "0")}`,
            }}
          />
        ))}
        <span>Mais</span>
      </div>
    </Paper>
  );
}
